import React, { ComponentPropsWithRef, ReactNode, forwardRef } from 'react'
import { InputText } from 'primereact/inputtext'
import { Tooltip } from 'primereact/tooltip'
import cn from 'classnames'
import { KeyFilterType } from 'primereact/keyfilter'
import { Message } from 'primereact/message'
import { IconField } from 'primereact/iconfield'
import { InputIcon } from 'primereact/inputicon'
interface InputTextProps extends ComponentPropsWithRef<'input'> {
    id: string
    label?: string
    infoText?: string
    error?: string
    subtitle?: ReactNode
    onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
    disabledReason?: string
    keyFilter?: KeyFilterType
    value?: string
    className?: string
    icon?: string
    'data-testid'?: string
}

export const Input = forwardRef<HTMLInputElement, InputTextProps>(
    ({ label, className, icon, keyFilter, infoText, disabledReason, ...props }: InputTextProps, ref) => {
        return (
            <div className={className}>
                <div className="flex justify-content-between">
                    <label
                        className={cn({ 'required-label': props.required }, 'p-label', { hidden: !label })}
                        htmlFor={props.id}
                    >
                        {label}
                    </label>
                    {infoText && (
                        <>
                            <Tooltip target=".custom-target-input" className="w-2" />
                            <i
                                style={{ color: 'var(--text-color-info)' }}
                                className="custom-target-input pi pi-info-circle cursor-pointer"
                                data-pr-tooltip={infoText}
                                data-pr-position="bottom"
                            ></i>
                        </>
                    )}
                </div>

                {icon ? (
                    <IconField iconPosition="left">
                        <InputIcon className={icon} />
                        <InputText
                            ref={ref}
                            autoComplete="off"
                            {...props}
                            value={props.value}
                            invalid={!!props.error}
                            className="w-full"
                            keyfilter={keyFilter}
                        />
                        {props.disabled && disabledReason && (
                            <span className="p-inputgroup-addon">
                                <Tooltip target=".custom-target-icon" />
                                <i
                                    className="custom-target-icon pi pi-lock cursor-pointer"
                                    data-pr-tooltip={disabledReason}
                                    data-pr-position="bottom"
                                ></i>
                            </span>
                        )}
                    </IconField>
                ) : (
                    <InputText
                        ref={ref}
                        autoComplete="off"
                        {...props}
                        value={props.value}
                        invalid={!!props.error}
                        className="w-full"
                    />
                )}

                {props.error && (
                    <Message
                        severity="error"
                        text={props.error}
                        data-testid={`${props['data-testid'] ?? 'input'}-error-message`}
                        className="mt-2"
                    />
                )}
                <small
                    id="help-text"
                    style={{ color: 'var(--text-color-secondary)' }}
                    data-testid={`${props['data-testid'] ?? 'input'}-help-text`}
                >
                    {props.subtitle}
                </small>
            </div>
        )
    }
)
